/**
 * EMPLOYMENT DETAILS STEP
 * 
 * Collects employment type, payment details, and number of stubs
 * ENHANCED: Added OVERTIME hours field (1.5x rate for hourly workers)
 * ENHANCED: Better bonus support for both hourly and salary
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Briefcase, DollarSign, Calendar, FileText, Clock, AlertCircle, Target, Calculator } from 'lucide-react';
import { STATE_TAX_DATA, calculatePayPeriodTaxes } from '../../utils/taxCalculations';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const US_STATES = Object.entries(STATE_TAX_DATA).map(([code, data]) => ({
  code,
  name: data.name
})).sort((a, b) => a.name.localeCompare(b.name));

const PAY_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly', description: 'Paid every week (52 pay periods/year)' },
  { value: 'bi-weekly', label: 'Bi-Weekly', description: 'Paid every other week (26 pay periods/year)' },
  { value: 'semi-monthly', label: 'Semi-Monthly', description: 'Paid twice per month (24 pay periods/year)' },
  { value: 'monthly', label: 'Monthly', description: 'Paid once per month (12 pay periods/year)' }
];

export default function EmploymentDetailsStep({ formData, updateFormData }) {
  const formatCurrency = (value) => {
    const num = value.replace(/[^\d.]/g, '');
    return num;
  };

  const getGrossPayPerPeriod = () => {
    // 2026-02-28 (user request) — Honour the per-check entry mode.
    // In 'per-check' mode we display whatever the admin typed as the
    // target per paycheck (treating gross/net consistently in the
    // summary strip).  In 'annual' mode we fall back to the previous
    // salary / hourly math.
    if (formData.payEntryMode === 'per-check') {
      return parseFloat(formData.perCheckAmount) || 0;
    }
    if (formData.paymentType === 'hourly') {
      const regularPay = (parseFloat(formData.hourlyRate) || 0) * (parseFloat(formData.hoursPerPeriod) || 0);
      const overtimePay = (parseFloat(formData.hourlyRate) || 0) * 1.5 * (parseFloat(formData.overtimeHours) || 0);
      return regularPay + overtimePay;
    } else {
      const periodsPerYear = {
        'weekly': 52,
        'bi-weekly': 26,
        'semi-monthly': 24,
        'monthly': 12
      };
      return (parseFloat(formData.annualSalary) || 0) / (periodsPerYear[formData.payFrequency] || 26);
    }
  };

  return (
    <div className="space-y-6">
      {/* Employment Type */}
      <div>
        <Label className="text-zinc-300 mb-3 block">Employment Type</Label>
        <RadioGroup
          value={formData.employmentType || 'employee'}
          onValueChange={(value) => updateFormData({ employmentType: value })}
          className="grid grid-cols-2 gap-4"
        >
          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            formData.employmentType === 'employee' 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="employee" id="employee" />
            <div>
              <Label htmlFor="employee" className="text-white cursor-pointer font-medium">Employee (W-2)</Label>
              <p className="text-xs text-zinc-500">Standard employment with tax withholdings</p>
            </div>
          </div>
          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            formData.employmentType === 'contractor' 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="contractor" id="contractor" />
            <div>
              <Label htmlFor="contractor" className="text-white cursor-pointer font-medium">Contractor (1099)</Label>
              <p className="text-xs text-zinc-500">Independent contractor (no withholdings)</p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Payment Type — hidden when the user picks per-check entry mode */}
      {formData.payEntryMode !== 'per-check' && (
      <div>
        <Label className="text-zinc-300 mb-3 block">Payment Type</Label>
        <RadioGroup
          value={formData.paymentType || 'hourly'}
          onValueChange={(value) => updateFormData({ paymentType: value })}
          className="grid grid-cols-2 gap-4"
        >
          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            formData.paymentType === 'hourly' 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="hourly" id="hourly" />
            <div>
              <Label htmlFor="hourly" className="text-white cursor-pointer font-medium">Hourly</Label>
              <p className="text-xs text-zinc-500">Paid per hour worked + overtime at 1.5x</p>
            </div>
          </div>
          <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            formData.paymentType === 'salary' 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="salary" id="salary" />
            <div>
              <Label htmlFor="salary" className="text-white cursor-pointer font-medium">Salary</Label>
              <p className="text-xs text-zinc-500">Fixed annual salary</p>
            </div>
          </div>
        </RadioGroup>
      </div>
      )}

      {/* 2026-02-28 (user request) — PAY ENTRY MODE PICKER.
          Mutually exclusive: user picks ONE path (annual OR target per
          paycheck).  Resolves the long-standing confusion where both
          Annual Salary AND Target Net/Gross were visible at the same
          time and the admin didn't know which drove the math.
          — Mode 'annual' (default) → show Hourly/Salary inputs below.
          — Mode 'per-check'        → hide hourly/salary; show a single
                                        Target per paycheck $ input with
                                        a Net/Gross toggle. */}
      <div>
        <Label className="text-zinc-300 mb-3 block flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-400" />
          How do you want to enter pay?
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1">pick one</span>
        </Label>
        <RadioGroup
          value={formData.payEntryMode || 'annual'}
          onValueChange={(value) => updateFormData({ payEntryMode: value })}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            (formData.payEntryMode || 'annual') === 'annual'
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="annual" id="mode-annual" className="mt-1" />
            <div>
              <Label htmlFor="mode-annual" className="text-white cursor-pointer font-medium">
                I know my annual salary / hourly rate
              </Label>
              <p className="text-xs text-zinc-500 mt-1">
                Enter your yearly pay (or hourly rate × hours). We'll calculate per-check and YTD — everything editable on the next page.
              </p>
            </div>
          </div>
          <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            formData.payEntryMode === 'per-check'
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
          }`}>
            <RadioGroupItem value="per-check" id="mode-per-check" className="mt-1" />
            <div>
              <Label htmlFor="mode-per-check" className="text-white cursor-pointer font-medium">
                I know what I want per paycheck
              </Label>
              <p className="text-xs text-zinc-500 mt-1">
                Enter a target amount per paycheck. We'll back-solve the yearly gross and YTD — everything editable on the next page.
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Pay Details */}
      <div className="border-t border-zinc-700 pt-6">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Pay Details
        </h4>

        {formData.payEntryMode === 'per-check' ? (
          /* 2026-02-28 (user request) — PER-CHECK MODE.  Single target
             amount input + Net/Gross toggle.  Hourly/Salary inputs are
             hidden to keep the choice mutually exclusive. */
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 flex items-center justify-between">
                <span>Target per paycheck ($) *</span>
                <div className="flex items-center gap-1 text-[11px] bg-zinc-800 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => updateFormData({ perCheckType: 'net' })}
                    className={`px-2.5 py-1 rounded transition-colors ${
                      (formData.perCheckType || 'net') === 'net'
                        ? 'bg-emerald-500 text-black font-semibold'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="per-check-type-net"
                  >
                    NET (take home)
                  </button>
                  <button
                    type="button"
                    onClick={() => updateFormData({ perCheckType: 'gross' })}
                    className={`px-2.5 py-1 rounded transition-colors ${
                      formData.perCheckType === 'gross'
                        ? 'bg-emerald-500 text-black font-semibold'
                        : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="per-check-type-gross"
                  >
                    GROSS (before tax)
                  </button>
                </div>
              </Label>
              <Input
                type="number"
                step="0.01"
                value={formData.perCheckAmount ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateFormData({ perCheckAmount: v === '' ? '' : parseFloat(v) });
                }}
                placeholder="2000"
                className="mt-1 bg-zinc-800 border-zinc-600 text-white text-lg font-semibold"
                data-testid="per-check-amount-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1.5">
                {(formData.perCheckType || 'net') === 'net'
                  ? 'This is what hits your bank account after taxes. We back-solve gross + yearly totals from here.'
                  : 'This is your gross pay before taxes. We derive taxes + net from your state tax rates.'}
              </p>
            </div>
          </div>
        ) : (formData.paymentType === 'hourly') ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-300">Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.hourlyRate ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateFormData({ hourlyRate: v === '' ? '' : parseFloat(v) });
                  }}
                  placeholder="20.00"
                  className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                  data-testid="hourly-rate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Regular Hours *</Label>
                <Input
                  type="number"
                  value={formData.hoursPerPeriod ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateFormData({ hoursPerPeriod: v === '' ? '' : parseFloat(v) });
                  }}
                  placeholder="80"
                  className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                  data-testid="hours-per-period-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-orange-400" />
                  Overtime Hours (1.5x)
                </Label>
                <Input
                  type="number"
                  value={formData.overtimeHours ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateFormData({ overtimeHours: v === '' ? '' : parseFloat(v) });
                  }}
                  placeholder="0"
                  className="mt-1 bg-zinc-800 border-orange-500/50 text-white"
                  data-testid="overtime-hours-input"
                />
                <p className="text-xs text-orange-400 mt-1">
                  Paid at {((parseFloat(formData.hourlyRate) || 0) * 1.5).toFixed(2)}/hr
                </p>
              </div>
            </div>
          </>
        ) : (
          <div>
            <Label className="text-zinc-300">Annual Salary ($) *</Label>
            <Input
              type="number"
              value={formData.annualSalary ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                // 2026-02-28 (user request) — stop snapping back to 52,000
                // while the user is typing.  Keep the raw string in state
                // so partial input ("5", "50", "500...") is preserved.
                updateFormData({ annualSalary: v === '' ? '' : parseFloat(v) });
              }}
              placeholder="52000"
              className="mt-1 bg-zinc-800 border-zinc-600 text-white"
              data-testid="annual-salary-input"
            />
          </div>
        )}
      </div>

      {/* Overtime Info Banner (only for hourly) */}
      {formData.paymentType === 'hourly' && formData.overtimeHours > 0 && (
        <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <p className="text-sm text-orange-300 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="font-medium">Overtime Calculated</span>
          </p>
          <p className="text-xs text-orange-200 mt-1">
            {formData.overtimeHours} hours × ${(formData.hourlyRate * 1.5).toFixed(2)}/hr = ${(formData.overtimeHours * formData.hourlyRate * 1.5).toFixed(2)}
          </p>
        </div>
      )}

      {/* Payment Frequency */}
      <div>
        <Label className="text-zinc-300 mb-3 block">Payment Frequency</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PAY_FREQUENCIES.map((freq) => (
            <div
              key={freq.value}
              onClick={() => updateFormData({ payFrequency: freq.value })}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                formData.payFrequency === freq.value
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
              data-testid={`pay-freq-${freq.value}`}
            >
              <p className="font-medium text-white">{freq.label}</p>
              <p className="text-xs text-zinc-500 mt-1">{freq.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tax State & Number of Stubs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-zinc-300">What state do you pay taxes in? *</Label>
          <Select 
            value={formData.taxState || 'CA'}
            onValueChange={(value) => updateFormData({ taxState: value })}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.name} {!STATE_TAX_DATA[state.code].hasStateTax && '(No State Tax)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-zinc-300">How many pay stubs do you need?</Label>
          <Select 
            value={String(formData.numberOfStubs || 1)}
            onValueChange={(value) => {
              const num = parseInt(value);
              updateFormData({ numberOfStubs: num });
              // Initialize per-stub hours array
              if (formData.paymentType === 'hourly') {
                const defaultHours = formData.hoursPerPeriod || 80;
                const newPerStubHours = Array(num).fill(defaultHours);
                updateFormData({ perStubHours: newPerStubHours });
              }
            }}
          >
            <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                <SelectItem key={num} value={String(num)}>
                  {num} paystub{num > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Per-Stub Hours Configuration - Only for hourly workers with multiple stubs */}
      {formData.paymentType === 'hourly' && formData.numberOfStubs > 1 && (
        <div className="border-t border-zinc-700 pt-6">
          <div className="flex items-center justify-between mb-4">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Configure Hours Per Stub
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">Use different hours per stub</span>
              <Switch
                checked={formData.usePerStubHours || false}
                onCheckedChange={(checked) => {
                  updateFormData({ usePerStubHours: checked });
                  if (checked && (!formData.perStubHours || formData.perStubHours.length !== formData.numberOfStubs)) {
                    // Initialize array with default hours
                    const defaultHours = formData.hoursPerPeriod || 80;
                    updateFormData({ perStubHours: Array(formData.numberOfStubs).fill(defaultHours) });
                  }
                }}
                data-testid="use-per-stub-hours-toggle"
              />
            </div>
          </div>
          
          {formData.usePerStubHours ? (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">
                Set different hours for each pay stub (e.g., 80 hrs, 75 hrs, 77 hrs)
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Array.from({ length: formData.numberOfStubs }).map((_, index) => (
                  <div key={index}>
                    <Label className="text-zinc-400 text-xs">Stub #{index + 1} Hours</Label>
                    <Input
                      type="number"
                      value={(formData.perStubHours && formData.perStubHours[index]) || formData.hoursPerPeriod || 80}
                      onChange={(e) => {
                        const newHours = [...(formData.perStubHours || Array(formData.numberOfStubs).fill(formData.hoursPerPeriod || 80))];
                        newHours[index] = parseFloat(e.target.value) || 0;
                        updateFormData({ perStubHours: newHours });
                      }}
                      className="mt-1 bg-zinc-900 border-amber-500/50 text-white text-center"
                      data-testid={`per-stub-hours-${index}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-zinc-500 bg-zinc-800/50 p-3 rounded">
              All {formData.numberOfStubs} stubs will use {formData.hoursPerPeriod || 80} hours each.
              Enable the toggle above to set different hours per stub.
            </p>
          )}
        </div>
      )}

      {/* 2026-06-23 — Target Pay calculator.  Hidden when the admin
          picks per-check entry mode — that mode already provides the
          target input + Net/Gross toggle, so this would be redundant
          (user asked for ONE path only). */}
      {formData.payEntryMode !== 'per-check' && (
        <TargetPayCalculator formData={formData} updateFormData={updateFormData} />
      )}

      {/* Pay Summary Preview */}
      <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <h4 className="text-sm font-semibold text-zinc-400 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Pay Summary Preview
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-zinc-500">Gross Pay / Period</p>
            <p className="text-lg font-bold text-green-400">
              ${getGrossPayPerPeriod().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            {formData.paymentType === 'hourly' && formData.overtimeHours > 0 && (
              <p className="text-xs text-orange-400">
                (incl. ${(formData.overtimeHours * formData.hourlyRate * 1.5).toFixed(2)} OT)
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-zinc-500">Pay Frequency</p>
            <p className="text-lg font-medium text-white capitalize">
              {(formData.payFrequency || 'bi-weekly').replace('-', ' ')}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Tax State</p>
            <p className="text-lg font-medium text-white">
              {STATE_TAX_DATA[formData.taxState]?.name || formData.taxState}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Stubs to Generate</p>
            <p className="text-lg font-medium text-amber-400">
              {formData.numberOfStubs}
            </p>
          </div>
        </div>
        
        {/* Info about Bonuses */}
        <div className="mt-4 pt-4 border-t border-zinc-700">
          <p className="text-xs text-zinc-400 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            <span className="font-medium">Bonuses & Additional Earnings</span>
          </p>
          <p className="text-xs text-zinc-500 mt-1 ml-6">
            You can add Bonuses, Commissions, and Tips on the next step (Paystub Details).
            These work for both Hourly and Salary employees.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * TargetPayCalculator — Lets the user type a Target Net (take-home) or
 * Target Gross paycheck. Backsolves the hourly rate (or annual salary)
 * needed to hit that target given the chosen pay frequency, then writes
 * the calculated value back to formData.hourlyRate / annualSalary so the
 * next step starts with realistic numbers.
 *
 * Iterative tax backsolve mirrors PaystubDetailsStep's reverse calculator
 * so the two stay numerically consistent.
 */
function TargetPayCalculator({ formData, updateFormData }) {
  const [mode, setMode] = React.useState('net'); // 'net' or 'gross'
  const [target, setTarget] = React.useState('');
  const [result, setResult] = React.useState(null);

  const periodsPerYear = {
    weekly: 52, 'bi-weekly': 26, 'semi-monthly': 24, monthly: 12,
  }[formData.payFrequency || 'bi-weekly'] || 26;

  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const grossFromNet = (targetNet) => {
    // Iterative solve: start at 1.25× target, refine 6 times.
    let g = targetNet * 1.25;
    for (let i = 0; i < 6; i++) {
      const t = calculatePayPeriodTaxes(
        g,
        formData.payFrequency || 'bi-weekly',
        formData.taxState || 'CA',
        formData.maritalStatus || 'single',
        formData.exemptions || 1,
        0
      );
      const totalTaxes = t.federalTax + t.stateTax + t.socialSecurity + t.medicare + t.sdi;
      const net = g - totalTaxes;
      g = g + (targetNet - net);
    }
    return r2(g);
  };

  const calculate = () => {
    const targetNum = parseFloat(String(target).replace(/[^0-9.\-]/g, ''));
    if (!Number.isFinite(targetNum) || targetNum <= 0) {
      toast.error('Type a target amount first');
      return;
    }
    // Determine the per-period GROSS needed
    const periodGross = mode === 'net' ? grossFromNet(targetNum) : targetNum;
    // Map back into hourly rate or annual salary
    if (formData.paymentType === 'hourly') {
      const hours = Number(formData.hoursPerPeriod) || 80;
      const rate = r2(periodGross / hours);
      updateFormData({ hourlyRate: rate });
      // Estimate net for the result banner so user sees confirmation
      const t = calculatePayPeriodTaxes(
        periodGross,
        formData.payFrequency || 'bi-weekly',
        formData.taxState || 'CA',
        formData.maritalStatus || 'single',
        formData.exemptions || 1,
        0
      );
      const taxes = t.federalTax + t.stateTax + t.socialSecurity + t.medicare + t.sdi;
      setResult({
        kind: 'hourly',
        rate,
        hours,
        periodGross: r2(periodGross),
        periodNet: r2(periodGross - taxes),
        annual: r2(rate * hours * periodsPerYear),
      });
      toast.success(`Calculated $${rate.toFixed(2)}/hr to hit your target`);
    } else {
      const annual = r2(periodGross * periodsPerYear);
      updateFormData({ annualSalary: annual });
      const t = calculatePayPeriodTaxes(
        periodGross,
        formData.payFrequency || 'bi-weekly',
        formData.taxState || 'CA',
        formData.maritalStatus || 'single',
        formData.exemptions || 1,
        0
      );
      const taxes = t.federalTax + t.stateTax + t.socialSecurity + t.medicare + t.sdi;
      setResult({
        kind: 'salary',
        annual,
        periodGross: r2(periodGross),
        periodNet: r2(periodGross - taxes),
      });
      toast.success(`Calculated $${annual.toLocaleString()} annual salary to hit your target`);
    }
  };

  const clear = () => {
    setTarget('');
    setResult(null);
  };

  return (
    <div className="p-4 rounded-lg border border-emerald-500/40 bg-emerald-500/5" data-testid="target-pay-calc">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex-shrink-0">
          <Target className="w-4 h-4 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="text-sm font-bold text-white">Target paycheck calculator</h4>
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5">Optional</span>
          </div>
          <p className="text-xs text-zinc-400">
            Want every paycheck to hit a specific take-home or gross amount? Type it here and we&apos;ll back into the {formData.paymentType === 'hourly' ? 'hourly rate' : 'annual salary'} that makes it work. You can still edit individual stubs on the next step.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr_auto] gap-2 items-stretch">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="h-10 px-3 rounded-md bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
          data-testid="target-pay-mode"
        >
          <option value="net">Target NET (take-home)</option>
          <option value="gross">Target GROSS</option>
        </select>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">$</span>
          <Input
            type="number"
            step="0.01"
            placeholder={mode === 'net' ? 'e.g., 2000 (per paycheck take-home)' : 'e.g., 2500 (per paycheck gross)'}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="h-10 pl-7 bg-zinc-900 border-zinc-700 text-white text-sm"
            data-testid="target-pay-amount"
          />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={calculate}
            className="h-10 bg-emerald-600 hover:bg-emerald-500 text-white text-sm whitespace-nowrap"
            data-testid="target-pay-calc-btn"
          >
            <Calculator className="w-4 h-4 mr-1.5" />
            Calculate
          </Button>
          {result && (
            <Button
              variant="outline"
              onClick={clear}
              className="h-10 border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-3 p-3 rounded-md bg-zinc-900 border border-emerald-500/30" data-testid="target-pay-result">
          {result.kind === 'hourly' ? (
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-xs text-zinc-400">Set hourly rate to</span>
              <span className="text-2xl font-bold text-emerald-300">${result.rate.toFixed(2)}/hr</span>
              <span className="text-xs text-zinc-500">
                · {result.hours} hours × {periodsPerYear} periods = ~${result.annual.toLocaleString()} per year
              </span>
              <span className="ml-auto text-xs text-zinc-400">
                Gross/period <span className="text-white font-semibold">${result.periodGross.toFixed(2)}</span> · Take-home <span className="text-emerald-300 font-semibold">${result.periodNet.toFixed(2)}</span>
              </span>
            </div>
          ) : (
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-xs text-zinc-400">Set annual salary to</span>
              <span className="text-2xl font-bold text-emerald-300">${result.annual.toLocaleString()}/yr</span>
              <span className="ml-auto text-xs text-zinc-400">
                Gross/period <span className="text-white font-semibold">${result.periodGross.toFixed(2)}</span> · Take-home <span className="text-emerald-300 font-semibold">${result.periodNet.toFixed(2)}</span>
              </span>
            </div>
          )}
          <p className="text-[11px] text-zinc-500 mt-2">
            ✓ The {result.kind === 'hourly' ? 'hourly rate' : 'salary'} field above was auto-updated. On the next step you can still edit any individual paystub if you need to tweak.
          </p>
        </div>
      )}
    </div>
  );
}
